import type {
	Command,
	GameState,
	PassCardsData,
	PlayCardData,
	PositionMap,
	SuitPoints,
	Trick,
} from './definitions';
import { GameAction, PassDirection } from './definitions';
import type { CardString } from './general_definitions';
import { Rank, Suit } from './general_definitions';
import type { CardObject } from './general_utils';
import { compareRank, getCardObjectFromString } from './general_utils';

export const WINNINGS_PILE_NAME_PREFIX = 'trickWinnings';

export function getPassDirectionFromGameState(gameState: GameState): PassDirection {
	const roundCount = gameState.rounds?.length ?? 0;
	const moddedRound = roundCount % 4;

	switch (moddedRound) {
		case 0:
			return PassDirection.Left;
		case 1:
			return PassDirection.Opposite;
		case 2:
			return PassDirection.Right;
		default:
			return PassDirection.None;
	}
}

export function getPassPositionByGameState(gameState: GameState, position: number): number {
	return ((position + gameState.rounds.length + 1) % gameState.playerCount);
}

/**
 * @deprecated Don't use this, use getPassPositionByGameState if possible
 * @param _gameState 
 * @param _position 
 */
export function getPassedFromPositionByGameState(_gameState: GameState, _position: number): number {

	throw new Error(`This function doesn't work correctly, don't use it`);
}

export function getPlayerHandName(position: number): string {
	return `hand${position}`;
}

export function getPlayerPassHandName(position: number): string {
	return `pass${position}`;
}

export function getTrickPileName(position: number): string {
	return `trick${position}`;
}

export function getScorePileName(position: number): string {
	return `${WINNINGS_PILE_NAME_PREFIX}${position}`;
}

export function getTrickWinner(trick: Trick): number {
	let currentWinningCard = trick?.cardsPlayed?.[trick.leadPlayer] ? getCardObjectFromString(trick.cardsPlayed[trick.leadPlayer]) : null;
	let currentWinner = trick.leadPlayer;

	if (currentWinningCard) {
		for (let player = 0; player < trick.cardsPlayed.length; ++player) {
			const card = trick?.cardsPlayed?.[player] ? getCardObjectFromString(trick.cardsPlayed[player]) : null;

			if (card && card.suit === currentWinningCard.suit && compareRank(card.rank as Rank, currentWinningCard.rank as Rank) > 0) {
				currentWinningCard = card;
				currentWinner = player;
			}
		}
	}

	return currentWinner;
}

export function isRoundOver(gameState: GameState): boolean {
	const playerHandName = getPlayerHandName(0);
	return gameState.deck.filter((c) => c.pile === playerHandName).length === 0;
}

export function dealCards(gameState: GameState): GameState {
	let currentPlayer = 0;

	for (const card of gameState.deck) {
		card.pile = getPlayerHandName(currentPlayer++);

		if (currentPlayer >= gameState.playerCount) {
			currentPlayer = 0;
		}
	}

	return gameState;
}

//Points will be indexed on player position, this doesn't track shoot-the-moon
export function calculateCardPoints(deck: CardObject[]): number[] {
	const suitPoints = calculateSuitPoints(deck);
	const { hearts, queen } = suitPoints;
	const score = Array.from(hearts);

	if (queen !== null) {
		score[queen] += 13;
	}

	return score;
}

export function calculateCardPointsHeartsOnly(deck: CardObject[]): number[] {
	const suitPoints = calculateSuitPoints(deck);
	const { hearts } = suitPoints;
	const score = Array.from(hearts);

	return score;
}

export function checkForQueenSpades(deck: CardObject[], position: number): boolean {
	// Check if the player has the queen of spades in their trick winnings
	const scorePile = getScorePileName(position);
	const hasQueenSpades = deck.some((c) =>
		c.pile === scorePile
		&& c.suit === Suit.Spades
		&& c.rank === 'Q',
	);

	return hasQueenSpades;
}

export function calculateSuitPoints(deck: CardObject[]): SuitPoints {
	const scores = [];
	let playerWithQueen = null;

	for (const card of deck) {
		if (card.pile.indexOf(WINNINGS_PILE_NAME_PREFIX) > -1 && ((card.rank === Rank.Queen && card.suit === Suit.Spades) || card.suit === Suit.Hearts)) {
			const decoded = decodePile(card.pile);

			if (decoded) {
				const { player } = decoded;

				if (typeof scores[player] === 'undefined') {
					scores[player] = 0;
				}

				scores[player] += card.suit === Suit.Hearts ? 1 : 0;

				if (card.suit === Suit.Spades) {
					playerWithQueen = player;
				}
			}

		}
	}

	return {
		hearts: scores,
		queen: playerWithQueen,
	};
}

export function decodePile(pile: string): { player: number, place: string } | null {
	const ind = pile.search(/\d+/);

	if (ind > -1) {
		return {
			player: parseInt(pile.substring(ind)),
			place: pile.substring(0, ind),
		};
	}

	return null;
}

export function makeToggleCardCommand(position: number, card: CardString): Command {
	const action: Command = {
		command: GameAction.Togglecard,
		data: {
			card,
		},
		position,
	};
	return action;
}

export function makePassCommand(position: number, cards: CardString[]): Command {
	const action: Command = {
		command: GameAction.PassCards,
		data: {
			cards,
		} as PassCardsData,
		position,
	};
	return action;
}

export function makePlayCardCommand(position: number, card: CardString): Command {
	const command: Command = {
		command: GameAction.PlayCard,
		data: {
			card,
		} as PlayCardData,
		position,
	};
	return command;
}

export function makeNextStageCommand(): Command {
	return {
		command: GameAction.NextStage,
		data: null!,
		position: null!,
	};
}

export function getPositionMap(currentPlayer: number, playerCount: number = 4): PositionMap {
	const pmap = {} as PositionMap;
	const positions = getPlayerPositions(currentPlayer, playerCount);

	for (const [player, position] of Object.entries(positions)) {
		pmap[position.toLowerCase() as keyof PositionMap] = getPlayerHandName(+player);
	}

	return pmap;
}

type PlaceName = 'Top' | 'Right' | 'Bottom' | 'Left';

export interface PlayerPosition {
	[key: number]: PlaceName;
}

export function getPlayerPositions(currentPlayer: number, playerCount = 4): PlayerPosition {
	const halfCount = Math.ceil(playerCount / 2);
	const result: PlayerPosition = {};

	const placeNames: PlaceName[] = [
		'Top',
		'Right',
		'Bottom',
		'Left',
	];

	for (let x = 0; x < playerCount; ++x) {
		const player = (x + currentPlayer + halfCount) % playerCount;
		result[player] = placeNames[x];
	}

	return result;
}

export const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const NUMBERS = '0123456789';

export function generateId(length = 6, characters = NUMBERS): string {
	let result = '';

	for (let i = 0; i < length; ++i) {
		result = `${result}${characters.charAt(Math.floor(Math.random() * characters.length))}`;
	}

	return result;
}
